import { defineStore } from 'pinia'
import { ptsApi, cmsApi } from '@/helpers/api'

export const usePtsSignUpStore = defineStore('PtsSignUpStore', {
  state: () => ({
    isLoggedIn: null,
    isSignedUp: false,
    errorMessageCode: null,
    ptsEventNo: '102',
    minimumLevelRequirement: 200, // at least one character selected must be this level or up to enable confirm button
    charactersList: [],
    selectedCharacters: [],
    dialog: {
      show: false,
    },
    mainPageSectionNumber: 10,
    newsList: [],
  }),
  getters: {
    messageText() {
      if (this.isSignedUp) {
        // success case
        this.mainPageSectionNumber = 30
        return 'Your sign-up request has been submitted for the PTS! Selected players will be notified via email.'
      } else if (this.errorMessageCode) {
        // error cases
        switch (this.errorMessageCode) {
          case 'gms-only':
            this.mainPageSectionNumber = 50
            return 'PTS sign-up is only for players living in the Global MapleStory service region.'

          case 'account-age':
            this.mainPageSectionNumber = 40
            return 'Only MapleStory accounts created before April 13, 2026 are eligible for the PTS.'

          case 'level-requirement':
            this.mainPageSectionNumber = 60
            return 'Only MapleStory accounts with a Lv. 200+ character in the NA region are eligible for the PTS.'

          case 'no-characters':
            this.mainPageSectionNumber = 70
            return 'Only MapleStory accounts with characters in the NA region are eligible for the PTS.'

          case 'game-banned':
            this.mainPageSectionNumber = 100
            return 'Only MapleStory accounts in good standing are eligible for the PTS.'

          default:
            try {
              window.dataLayer.push({
                event: 'standardMicrositeTrackingEvent',
                interaction: 'section_view',
                micrositeSectionName: 'Main Page',
                micrositeSectionNumber: 110,
              })
            } catch {
              // do nothing
            }
            return 'An error has occurred. Please try again later.'
        }
      }

      return ''
    },
  },
  actions: {
    login() {
      const returnUrl = new window.URL(window.location.href)
      returnUrl.searchParams.set('ptssignup', 'true')
      window.nexon.sso.login(returnUrl.toString())
    },

    async getUserCharacters() {
      try {
        this.charactersList = []
        const { data } = await ptsApi.get('/characters')
        this.charactersList = data.naCharacters
        this.dialog.show = true
      } catch (e) {
        this.dialog.show = false
        this.errorMessageCode = 'error'
        throw e
      }
    },

    async checkUserEligibility(showDialog = false) {
      try {
        const { data } = await ptsApi.get(`/events/${this.ptsEventNo}/eligibility`)
        if (data.isEligible) {
          if (showDialog) this.getUserCharacters()
        } else if (showDialog) {
          const errorCode = data.errorCode
          switch (errorCode) {
            case 'ACCOUNT_TOO_YOUNG':
              this.errorMessageCode = 'account-age'
              break
            case 'GAME_BANNED':
              this.errorMessageCode = 'game-banned'
              break
            case 'NO_CHARACTERS':
              this.errorMessageCode = 'no-characters'
              break
            case 'LEVEL_LIMITED':
              this.errorMessageCode = 'level-requirement'
              break
            default:
              this.errorMessageCode = 'error'
          }
        }
      } catch {
        if (showDialog) this.errorMessageCode = 'error'
      }
    },

    async checkUserStatus(showDialog = false) {
      try {
        await ptsApi.get(`/events/${this.ptsEventNo}/user`)
        this.isLoggedIn = true
        this.isSignedUp = true
      } catch (e) {
        if (e && e.response) {
          const errorCode = e.response.headers?.['x-arena-web-errorcode']

          if (e.response.status === 401) {
            // NOT LOGGED IN USER
            this.isLoggedIn = false
            if (showDialog) this.login()
            return
          } else if (e.response.status === 403 && errorCode === '22002') {
            // IP BLOCKED REGION
            this.isLoggedIn = true
            this.isSignedUp = false
            if (showDialog) this.errorMessageCode = 'gms-only'
            return
          } else if (e && e.response && e.response.status === 404 && errorCode === '22025') {
            // USER LOGGED IN BUT NOT SIGNED UP
            this.isLoggedIn = true
            this.isSignedUp = false

            await this.checkUserEligibility(showDialog)
          } else {
            if (showDialog) this.errorMessageCode = 'error'
            throw e
          }
        }
      }
    },

    async signUp() {
      try {
        // TODO
      } catch {
        // TODO
      }
    },

    toggleSelectedCharacter(character) {
      const index = this.selectedCharacters.findIndex((c) => c.characterId === character.characterId)
      if (index === -1) {
        if (this.selectedCharacters.length < 3) this.selectedCharacters.push(character)
      } else {
        this.selectedCharacters.splice(index, 1)
      }
    },

    async sendSelectedCharacters() {
      try {
        const data = this.selectedCharacters.map((char) => ({
          characterId: char.characterId,
          worldId: char.worldId,
        }))

        await ptsApi.post(`/events/${this.ptsEventNo}/characters`, {
          naCharacters: data,
          euCharacters: [],
        })

        this.isSignedUp = true
      } catch {
        this.isSignedUp = false
        this.errorMessageCode = 'error'
      }
    },

    async fetchNewsList() {
      try {
        this.newsList = []
        const { data } = await cmsApi.get(`/pts-news`)
        this.newsList = [...data].sort((a, b) => Number(b.featured) - Number(a.featured))
      } catch {
        // do nothing, news section will simply not show if there's an error fetching news data
      }
    },
  },
})
